/**
 * useSolomonPlanner — natural-language → typed action proposals.
 *
 * Thin wrapper around the `solomon-plan` edge function. The hook
 * doesn't render anything itself; it returns `plan(utterance)` and
 * lets the caller decide what to do with the result.
 *
 * Typical usage:
 *
 *   const { plan, isPlanning, lastResponse } = useSolomonPlanner();
 *   const { prepare } = useActionEngine({ defaultSource: "solomon" });
 *
 *   async function onSubmit(text: string) {
 *     const result = await plan({ utterance: text, context: { ... } });
 *     if (!result) return;
 *     // For each proposal, prepare via the engine; render
 *     // ActionProposalCard per prepared object.
 *     const prepared = await Promise.all(
 *       result.proposals.map((p) => prepare(p))
 *     );
 *     setCards(prepared);
 *   }
 */

import { useCallback, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { invokeEdgeFunction } from "@/lib/edge-functions";
import { useOrganization } from "@/hooks/useOrganization";
import { useCurrentPerson } from "@/hooks/useCurrentPerson";
import { useFeatureGate } from "@/hooks/useTierGate";
import type { ActionProposal } from "@/lib/solomon-action-engine";

const UPGRADE_REQUIRED_REPLY =
  "Solomon's action planner is available on the Flourish plan. Upgrade in Settings → Subscription to unlock AI-assisted actions.";

export interface PlanContextHints {
  /** Override the auto-detected page path. */
  page?: string;
  /**
   * The entity the user is currently viewing. At most one of each
   * pair is meaningful — the planner system prompt lets the LLM
   * resolve "this person" / "this group" / "this request" / "this
   * household" against whichever id is populated.
   *
   * Pages push these via `useSolomonFocus`. Singleton: last page
   * to register wins; unmount clears.
   */
  focusedPersonId?: string;
  focusedPersonName?: string;
  focusedGroupId?: string;
  focusedGroupName?: string;
  focusedRequestId?: string;
  focusedRequestTitle?: string;
  focusedHouseholdId?: string;
  focusedHouseholdName?: string;
  /**
   * The caller's own `people.id` + display name. Auto-filled by the
   * `useSolomonPlanner` hook from `useCurrentPerson()` so the planner
   * can resolve "me" / "I" / "assign this to me" against the right
   * id without a clarifying round-trip. Undefined for callers
   * without a linked `people` row (rare — usually just brand-new
   * orgs before bootstrap).
   *
   * NOTE: this is distinct from the `focused*` fields. Focus is the
   * entity the user is *viewing*; caller is the entity the user *is*.
   * Both can be populated at the same time and the planner uses
   * them for different referents.
   */
  callerPersonId?: string;
  callerPersonName?: string;
}

export interface PlanRequest {
  utterance: string;
  conversation?: Array<{ role: "user" | "assistant"; content: string }>;
  context?: PlanContextHints;
}

/**
 * Raw proposal returned by the edge function. Distinct from
 * ActionProposal so we can carry the planner's rationale forward
 * before the engine takes ownership.
 */
interface PlannerProposalDTO {
  actionId: string;
  params: Record<string, unknown>;
  rationale?: string | null;
  plannedForOrganizationId?: unknown;
}

export interface PlannerResponse {
  /** Server-owned tenant binding for this entire planner response. */
  plannedForOrganizationId: string;
  proposals: Array<ActionProposal<Record<string, unknown>>>;
  freeform_reply: string | null;
  clarifying_question: string | null;
}

interface RawPlanResponse {
  plannedForOrganizationId?: unknown;
  proposals: PlannerProposalDTO[];
  freeform_reply?: string | null;
  clarifying_question?: string | null;
}

export function useSolomonPlanner() {
  const [isPlanning, setIsPlanning] = useState(false);
  const [lastResponse, setLastResponse] = useState<PlannerResponse | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const { organizationId } = useOrganization();
  // A planner request can outlive the render that started it. Check the
  // response against this latest tenant before returning any model text or
  // proposals, since either can include tenant-specific context.
  const organizationIdRef = useRef(organizationId);
  organizationIdRef.current = organizationId;
  // Auto-fill caller context so the planner can resolve "me" / "I"
  // without a clarifying round-trip. Explicit `context.callerPerson*`
  // overrides per-field (consistent with the existing focus prop
  // semantics).
  const { data: currentPerson } = useCurrentPerson();
  // Solomon AI is a Flourish-tier feature. useSolomonChat.sendMessage
  // gates the same way; the planner previously had no gate at all,
  // letting free-tier orgs execute real mutations via the action engine.
  // This is a client-side fast path only — the edge function is the
  // authoritative gate (see solomon-plan/index.ts), so we only
  // short-circuit here when the entitlement query has actually resolved
  // to "not entitled". `hasFeature` also defaults to false while the
  // query is still loading OR if it failed outright (e.g. a transient
  // PostgREST error, which settles isLoading to false without ever
  // confirming a real "no entitlement") — both would otherwise falsely
  // upgrade-nudge an entitled org with no retry; proceeding either way
  // lets the server decide instead of guessing.
  const {
    hasFeature: solomonEnabled,
    isLoading: isGateLoading,
    isError: isGateError,
  } = useFeatureGate("solomon_ai");

  const plan = useCallback(
    async (req: PlanRequest): Promise<PlannerResponse | null> => {
      if (!solomonEnabled && !isGateLoading && !isGateError) {
        const response: PlannerResponse = {
          plannedForOrganizationId: organizationId ?? "",
          proposals: [],
          freeform_reply: UPGRADE_REQUIRED_REPLY,
          clarifying_question: null,
        };
        setLastResponse(response);
        return response;
      }
      setIsPlanning(true);
      setError(null);
      try {
        const requestOrganizationId = organizationId?.trim() || null;
        // Auto-fill caller from useCurrentPerson; explicit prop
        // override wins per-field. Caller name is the formatted
        // "First Last" or undefined if neither is set.
        const autoCallerName = currentPerson
          ? [currentPerson.first_name ?? "", currentPerson.last_name ?? ""]
              .map((s) => s.trim())
              .filter(Boolean)
              .join(" ") || undefined
          : undefined;

        const body = {
          utterance: req.utterance,
          conversation: req.conversation ?? [],
          context: {
            page: req.context?.page ?? location.pathname,
            focusedPersonId: req.context?.focusedPersonId,
            focusedPersonName: req.context?.focusedPersonName,
            focusedGroupId: req.context?.focusedGroupId,
            focusedGroupName: req.context?.focusedGroupName,
            focusedRequestId: req.context?.focusedRequestId,
            focusedRequestTitle: req.context?.focusedRequestTitle,
            focusedHouseholdId: req.context?.focusedHouseholdId,
            focusedHouseholdName: req.context?.focusedHouseholdName,
            callerPersonId:
              req.context?.callerPersonId ?? currentPerson?.id,
            callerPersonName:
              req.context?.callerPersonName ?? autoCallerName,
          },
          organization_id: organizationId ?? undefined,
        };

        const { data, error: invokeError } = await invokeEdgeFunction<
          RawPlanResponse
        >("solomon-plan", {
          body,
          policy: "interactive",
        });

        if (invokeError) {
          // The client-side gate above is a fast path only — solomon-plan
          // is the authoritative check and can reject with 403 even when
          // solomonEnabled looked true here (a cached/stale entitlement,
          // or the loading-race case above). solomon-plan returns 403
          // only for this gate today, so translate it into the same
          // upgrade-nudge shape instead of a generic "couldn't process"
          // error the composer would otherwise show.
          if (invokeError.status === 403) {
            // The error reply is still organization-scoped (and can outlive
            // the request), so it needs the same live-tenant guard as a
            // successful response. Never show A's upgrade state after the
            // user has already switched to B.
            if (
              !requestOrganizationId ||
              organizationIdRef.current !== requestOrganizationId
            ) {
              setError(
                "Organization changed while Solomon was planning. Please try again.",
              );
              return null;
            }
            const response: PlannerResponse = {
              plannedForOrganizationId: requestOrganizationId,
              proposals: [],
              freeform_reply: UPGRADE_REQUIRED_REPLY,
              clarifying_question: null,
            };
            setLastResponse(response);
            return response;
          }
          setError(invokeError.message ?? "Planner unavailable.");
          return null;
        }
        if (!data) {
          setError("Planner returned empty response.");
          return null;
        }

        const responseOrganizationId =
          typeof data.plannedForOrganizationId === "string"
            ? data.plannedForOrganizationId.trim()
            : null;
        // Do not surface even a freeform/clarifying response unless the edge
        // bound the complete response to the tenant that initiated this
        // request and that tenant is still current. This fails closed for
        // older edge responses during a rolling deploy.
        if (
          !requestOrganizationId ||
          !responseOrganizationId ||
          responseOrganizationId !== requestOrganizationId ||
          organizationIdRef.current !== requestOrganizationId
        ) {
          setError("Organization changed while Solomon was planning. Please try again.");
          return null;
        }

        // Translate DTO → ActionProposal[]. The engine's prepare()
        // takes it from here.
        const response: PlannerResponse = {
          plannedForOrganizationId: responseOrganizationId,
          proposals: data.proposals.map((p) => ({
            actionId: p.actionId,
            params: p.params,
            rationale: p.rationale ?? null,
            source: "solomon" as const,
            plannedForOrganizationId:
              typeof p.plannedForOrganizationId === "string"
                ? p.plannedForOrganizationId
                : undefined,
          })),
          freeform_reply: data.freeform_reply ?? null,
          clarifying_question: data.clarifying_question ?? null,
        };
        setLastResponse(response);
        return response;
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        return null;
      } finally {
        setIsPlanning(false);
      }
    },
    [
      location.pathname,
      organizationId,
      currentPerson?.id,
      currentPerson?.first_name,
      currentPerson?.last_name,
      solomonEnabled,
      isGateLoading,
      isGateError,
    ],
  );

  const reset = useCallback(() => {
    setLastResponse(null);
    setError(null);
  }, []);

  return { plan, isPlanning, lastResponse, error, reset };
}
